import 'react-multi-carousel/lib/styles.css';

import { useSiteContext, useUserContext } from 'src/store';
import {
  IAppDetailsTranslations,
  ITranslatedTextAll,
  ButtonsStrip,
  ClinicalFeatures,
  ConditionAreas,
  CoreDetails,
  DeveloperDescription,
  DocumentRequestWidget,
  PlatformTabs,
  useAppDetails,
  SchemeStrip,
} from '@orcha/micro-library-app-details-ui';
import { PlatformNameConverter } from '@orcha/utils';
import Breadcrumbs from 'src/components/Breadcrumbs';
import { Divider } from '@mui/material';
import DownloadDialog from 'src/components/DownloadDialog';
import Grid from '@mui/material/Grid';
import useTranslation from 'next-translate/useTranslation';
import useStorage from 'src/hooks/useStorage';
import Image from 'next/image';
import { ContainedPage } from '../../common/styles/PageLayout';
import { PageWithContextProps } from './AppDetailsPage.types';

const fallbackImageURL = '/app_screenshot_fallback.svg';
const fallbackLogoImageURL = '/app_logo_fallback.jpg';

const appDetailsWidgetSelector = {
  coreDetails: CoreDetails,
  clinicalFeatures: ClinicalFeatures,
  conditionAreas: ConditionAreas,
  developerDescription: DeveloperDescription,
  buttons: ButtonsStrip,
  platformTabs: PlatformTabs,
  documentRequest: DocumentRequestWidget,
  schemeStrip: SchemeStrip,
};

export const PageWithContext = ({
  breadcrumbUrl,
  locale,
  basePath,
  asPath,
  hostUrl,
}: PageWithContextProps) => {
  const {
    app,
    widgets,
    isDownloadDialogOpen,
    setIsDownloadDialogOpen,
    eventHandlers,
    dynamicContentApiUrl,
    dynamicContent,
  } = useAppDetails();

  const { getItem } = useStorage();
  const { user } = useUserContext();
  const orderedWidgets = widgets?.slice().sort((a, b) => a.order - b.order);
  let breadcrumbText: string | undefined;
  const { t } = useTranslation();
  const { showCommissionableStatus, assureEnabled } = useSiteContext();

  if (!app || !widgets || widgets.length === 0) return null;

  const { appId, name, platforms, logoUrl, cost, appStatus } = app;

  if (breadcrumbUrl?.includes('topic')) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const prevTopicPageBreadcrumb = (
      getItem('topicPageBreadcrumb', 'session') as any
    )?.breadcrumbText;
    breadcrumbText = prevTopicPageBreadcrumb || t('breadcrumbs.topic');
  }

  const handleDownloadDialogClose = () => {
    setIsDownloadDialogOpen(false);
  };

  const appDetailsTranslations: IAppDetailsTranslations = {
    platformScoreDetailsHeading: t('appDetails:scoreDetailsHeading'),
    platformScoreDetailsMissingDynamicContent: t(
      'appDetails:missingDynamicContent'
    ),
    platformReviewWarnings: {
      ios: t('appDetails:reviewWarning', {
        platform: 'iOS',
      }),
      android: t('appDetails:reviewWarning', {
        platform: 'Android',
      }),
    },
    developerLabel: t('appDetails:developerLabel'),
    platformIconsTitle: t('appDetails:platformsIconTitle'),
    platformIconTitle: {
      ios: t('appDetails:specificPlatformIconTitle', {
        platform: 'iOS',
      }),
      android: t('appDetails:specificPlatformIconTitle', {
        platform: 'Android',
      }),
      web: t('appDetails:specificPlatformIconTitle', {
        platform: 'Web',
      }),
    },
    platformLabels: {
      ios: t('appCommon:platformLabels.ios'),
      android: t('appCommon:platformLabels.android'),
      web: t('appCommon:platformLabels.web'),
    },
    costIconTitle: t('appCommon:costIconTitle'),
    appBenefitsBanner: {
      title: {
        commissionableOnly: t(
          'microsite:appBenefitsBanner.title.CommissionableOnly'
        ),
        commissionable: t('microsite:appBenefitsBanner.title.Commissionable'),
        commissioned: t('microsite:appBenefitsBanner.title.Commissioned'),
        discountedCost: t('microsite:appBenefitsBanner.title.DiscountedCost'),
        freeTrialPeriod: t('microsite:appBenefitsBanner.title.FreeTrialPeriod'),
      },
      body: {
        commissionableOnly: t(
          'microsite:appBenefitsBanner.bodyText.CommissionableOnly'
        ),
        commissionable: t(
          'microsite:appBenefitsBanner.bodyText.Commissionable'
        ),
        commissioned: t('microsite:appBenefitsBanner.bodyText.Commissioned'),
        discountedCost: t(
          'microsite:appBenefitsBanner.bodyText.DiscountedCost'
        ),
        freeTrialPeriod: t(
          'microsite:appBenefitsBanner.bodyText.FreeTrialPeriod'
        ),
      },
    },
    appCostLabels: {
      commissionable: t('microsite:appCostLabels.Commissionable'),
      commissioned: t('microsite:appCostLabels.Commissioned'),
      discountedCost: t('microsite:appCostLabels.DiscountedCost'),
      freeTrialPeriod: t('microsite:appCostLabels.FreeTrialPeriod'),
    },
  };

  const widgetComponentsTranslatedTextAll: ITranslatedTextAll = {
    platformScoreSummaryHeading: t('appDetails:scoreSummaryHeading'),
    platformScoreSummaryLevelText: {
      scoreSummaryLevelText_0: t('appDetails:scoreSummaryLevelText_0'),
      scoreSummaryLevelText_1: t('appDetails:scoreSummaryLevelText_1'),
      scoreSummaryLevelText_2: t('appDetails:scoreSummaryLevelText_2'),
      scoreSummaryLevelText_3: t('appDetails:scoreSummaryLevelText_3'),
      scoreSummaryLevelText_4: t('appDetails:scoreSummaryLevelText_4'),
    },
    platformAccreditationsHeading: t('appDetails:accreditationsHeading'),
    //
    accreditationBadgeTooltipText: t('appDetails:accreditationAltText'),
    //
    platformGalleryHeading: t('appDetails:galleryHeading'),
    platformGalleryMissingImages: t('appDetails:missingGalleryImages'),

    platformGeneralFeaturesHeading: t('appDetails:generalFeaturesHeading'),
    platformInformationSliceTitle: t('appDetails:information.title'),

    platformInformationSliceDeveloperLabel: t(
      'appDetails:information.developerLabel'
    ),
    platformInformationSliceSizeLabel: t('appDetails:information.sizeLabel'),
    platformInformationSliceCountryLabel: t(
      'appDetails:information.countryLabel'
    ),
    platformInformationSliceLastUpdatedLabel: t(
      'appDetails:information.lastUpdatedLabel'
    ),
    platformInformationSliceAppVersionLabel: t(
      'appDetails:information.appVersionLabel'
    ),
    platformInformationSliceOrchaReviewDateLabel: t(
      'appDetails:information.orchaReviewDateLabel'
    ),
    platformScoreDetailsHeading: t('appDetails:scoreDetailsHeading'),
    platformScoreDetailsMissingDynamicContent: t(
      'appDetails:missingDynamicContent'
    ),
    scoreDetailsViewHeading: t('appDetails:scoreDetailsHeading'),
    scoreDetailsViewMissingDynamicContent: t(
      'appDetails:missingDynamicContent'
    ),
    thresholdImageWidgetTooltipText: '',
  };

  const buttonsStripTranslatedText = {
    removeFavouriteButton: {
      label: t('proaccount:appFavourites.removeFromFavouritesButton'),
      title: t('proaccount:appFavourites.removeFromFavouritesButton'),
    },
    addFavouriteButton: {
      label: t('proaccount:appFavourites.addToFavouritesButton'),
      title: t('proaccount:appFavourites.addToFavouritesButton'),
    },

    recommendButton: {
      label: t('appDetails:recommendButton'),
      title: t('appDetails:recommendButtonTitle'),
    },

    downloadButton: {
      label: t('appDetails:downloadButton'),
      title: t('appDetails:downloadButtonTitle'),
    },

    getLinkButton: {
      label: t('appDetails:copyUrlButton'),
      title: t('appDetails:copyUrlButtonTitle'),
    },
    developerDescriptionHeading: t('appDetails:descriptionHeading'),
    developerDescriptionShowMoreButton: t(
      'appDetails:descriptionShowMoreButton'
    ),
    developerDescriptionShowLessButton: t(
      'appDetails:descriptionShowLessButton'
    ),
    conditionAreasHeading: t('appDetails:conditionAreasHeading'),
    clinicalFeaturesHeading: t('appDetails:clinicalFeaturesHeading'),
    schemesHeading: t('appDetails:schemesHeading'),
  };

  const renderContainedPage = () => (
    <ContainedPage sx={{ minHeight: 400 }}>
      <Grid container spacing={2} data-testid="app-details-page-container">
        <Grid item xs={12}>
          {orderedWidgets?.map((widget, index) => {
            const WidgetComponent = appDetailsWidgetSelector[widget.name];
            const shouldShowDivider = index + 1 <= orderedWidgets.length - 1;
            return WidgetComponent ? (
              <>
                <WidgetComponent
                  key={`${widget.name}-${widget.order}`}
                  {...(widget.widgets && {
                    widgets: widget.widgets,
                  })}
                  app={app}
                  configuration={widget?.configuration}
                  user={user}
                  {...(eventHandlers && {
                    eventHandlers,
                  })}
                  dynamicContentApiUrl={dynamicContentApiUrl}
                  data-testid={`app-details-widget-${widget.order}`}
                  translations={appDetailsTranslations}
                  showCommissionableStatus={showCommissionableStatus}
                  // Pass all possible props - components will ignore what they don't need
                  FlexibleImageComponent={Image}
                  fallbackLogoImageURL={fallbackLogoImageURL}
                  fallbackImageURL={fallbackImageURL}
                  locale={locale}
                  origin={hostUrl}
                  basePath={basePath}
                  asPath={asPath}
                  translatedText={buttonsStripTranslatedText}
                  translatedTextAll={widgetComponentsTranslatedTextAll}
                  assureEnabled={assureEnabled}
                  dynamicContent={dynamicContent}
                />

                {shouldShowDivider && <Divider />}
              </>
            ) : null;
          })}
        </Grid>
      </Grid>
    </ContainedPage>
  );

  return (
    <>
      <Breadcrumbs
        breadcrumbs={[
          {
            text: t('breadcrumbs.home'),
            href: '/',
          },
          {
            text: breadcrumbText ?? t('breadcrumbs.search'),
            href: breadcrumbUrl ?? '/search',
          },
          {
            text: app.name,
            disabled: true,
          },
        ]}
      />
      {renderContainedPage()}
      <DownloadDialog
        app={{
          appId,
          name,
          logoUrl,
          cost,
          platforms: platforms.map((p) => {
            return {
              platformName: PlatformNameConverter(p.platformName),
              downloadLink: p.downloadLink,
            };
          }),
          appStatus,
        }}
        open={isDownloadDialogOpen}
        handleClose={handleDownloadDialogClose}
      />
    </>
  );
};
